import React, { useState, useEffect } from "react";
import api from "@/services/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MessageCircle, Save, Loader2, Send, Zap, Users, FileText, History, Play } from "lucide-react";

const TIPO_LABEL = {
  bienvenida: "Bienvenida", subida_nivel: "Subida de nivel", inactivo_30: "Inactivo 30 días",
  inactivo_60: "Inactivo 60 días", inactivo_90: "Inactivo 90 días",
  beneficio_vence: "Regalo por vencer", cumpleanos: "Cumpleaños", prueba: "Prueba",
};

export default function AdminClubMarketing() {
  const [config, setConfig] = useState(null);
  const [plantillas, setPlantillas] = useState([]);
  const [segmentos, setSegmentos] = useState(null);
  const [envios, setEnvios] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [ejecutando, setEjecutando] = useState(false);
  const [testTel, setTestTel] = useState("");
  const [enviandoTest, setEnviandoTest] = useState(false);

  const cargar = () => {
    api.get("/club/marketing/config").then((r) => setConfig(r.data));
    api.get("/club/marketing/plantillas").then((r) => setPlantillas(r.data));
    api.get("/club/marketing/segmentos").then((r) => setSegmentos(r.data));
    api.get("/club/marketing/envios").then((r) => setEnvios(r.data));
  };
  useEffect(cargar, []);

  const setAuto = (k, v) => setConfig({ ...config, automatizaciones: { ...config.automatizaciones, [k]: v } });
  const setSeg = (idx, k, v) => {
    const segs = [...config.segmentos_inactivos];
    segs[idx] = { ...segs[idx], [k]: v };
    setConfig({ ...config, segmentos_inactivos: segs });
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      const r = await api.put("/club/marketing/config", config);
      setConfig(r.data);
      toast.success("Configuración guardada");
    } catch { toast.error("Error guardando"); } finally { setGuardando(false); }
  };

  const ejecutar = async () => {
    setEjecutando(true);
    try {
      const r = await api.post("/club/marketing/ejecutar");
      const res = r.data.resultado;
      toast.success(`Ejecutado: ${res.inactivos} inactivos, ${res.por_vencer} por vencer, ${res.cumpleanos} cumpleaños, ${res.fallidos} fallidos`);
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || "Error ejecutando"); } finally { setEjecutando(false); }
  };

  const enviarTest = async () => {
    setEnviandoTest(true);
    try {
      await api.post("/club/marketing/test", { telefono: testTel });
      toast.success("Mensaje de prueba enviado por WhatsApp");
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo enviar"); } finally { setEnviandoTest(false); }
  };

  const guardarPlantilla = async (p) => {
    try {
      await api.put(`/club/marketing/plantillas/${p.id}`, { nombre: p.nombre, mensaje: p.mensaje, activo: p.activo });
      toast.success(`Plantilla "${TIPO_LABEL[p.tipo]}" guardada`);
    } catch { toast.error("Error guardando plantilla"); }
  };

  if (!config) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-torta-brown" /></div>;
  const auto = config.automatizaciones || {};

  return (
    <div className="space-y-5 max-w-5xl" data-testid="admin-club-whatsapp-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl text-torta-dark flex items-center gap-2"><MessageCircle className="w-7 h-7 text-green-600" /> Club WhatsApp</h1>
          <p className="text-torta-muted text-sm">Automatizaciones, segmentos y recuperación de clientes inactivos</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2"><Label className="text-xs">Activo</Label>
            <Switch data-testid="mkt-activo-switch" checked={config.activo} onCheckedChange={(v) => setConfig({ ...config, activo: v })} /></div>
          <Button data-testid="mkt-ejecutar-btn" variant="outline" onClick={ejecutar} disabled={ejecutando}>
            {ejecutando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />} Ejecutar ahora
          </Button>
          <Button data-testid="mkt-guardar-btn" onClick={guardar} disabled={guardando} className="bg-torta-brown hover:bg-torta-brown/90">
            {guardando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Guardar
          </Button>
        </div>
      </div>

      <Tabs defaultValue="auto">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="auto" data-testid="tab-automatizaciones"><Zap className="w-3.5 h-3.5 mr-1" /> Automatizaciones</TabsTrigger>
          <TabsTrigger value="segmentos" data-testid="tab-segmentos"><Users className="w-3.5 h-3.5 mr-1" /> Segmentos</TabsTrigger>
          <TabsTrigger value="plantillas" data-testid="tab-plantillas"><FileText className="w-3.5 h-3.5 mr-1" /> Plantillas</TabsTrigger>
          <TabsTrigger value="historial" data-testid="tab-historial"><History className="w-3.5 h-3.5 mr-1" /> Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="auto" className="space-y-4 mt-4">
          <div className="bg-white rounded-2xl p-5 shadow-sm space-y-3">
            <h2 className="font-display text-lg text-torta-dark">Campañas de inactivos (automáticas, diarias 10am)</h2>
            {config.segmentos_inactivos.map((s, idx) => (
              <div key={s.id} className="flex items-center gap-3 border border-torta-sand rounded-xl px-4 py-2.5" data-testid={`seg-config-${s.id}`}>
                <Switch checked={s.activo} onCheckedChange={(v) => setSeg(idx, "activo", v)} />
                <span className="text-sm font-semibold text-torta-dark flex-1">Clientes sin comprar hace</span>
                <Input type="number" min={1} className="w-20 h-8" value={s.dias} onChange={(e) => setSeg(idx, "dias", Math.max(1, parseInt(e.target.value) || 1))} />
                <span className="text-xs text-torta-muted">días</span>
              </div>
            ))}
            <div className="flex items-center gap-2 text-xs text-torta-muted">
              <span>Mínimo</span>
              <Input type="number" className="w-16 h-7" value={config.dias_minimos_entre_mensajes}
                onChange={(e) => setConfig({ ...config, dias_minimos_entre_mensajes: parseInt(e.target.value) || 7 })} />
              <span>días entre mensajes al mismo cliente · cada campaña se envía 1 sola vez por ciclo de inactividad</span>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-sm space-y-3">
            <h2 className="font-display text-lg text-torta-dark">Automatizaciones por eventos</h2>
            {[["bienvenida", "Bienvenida al Club tras la primera compra"],
              ["subida_nivel", "Felicitación al subir de nivel (con su código de regalo)"],
              ["beneficio_por_vencer", "Aviso cuando un regalo está por vencer"],
              ["cumpleanos", "Cumpleaños con beneficio especial"]].map(([k, l]) => (
              <div key={k} className="flex items-center justify-between border border-torta-sand rounded-xl px-4 py-2.5">
                <span className="text-sm text-torta-dark">{l}</span>
                <Switch data-testid={`auto-${k}-switch`} checked={!!auto[k]} onCheckedChange={(v) => setAuto(k, v)} />
              </div>
            ))}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label className="text-xs">Avisar vencimiento con (días)</Label>
                <Input type="number" value={auto.dias_aviso_vencimiento} onChange={(e) => setAuto("dias_aviso_vencimiento", parseInt(e.target.value) || 5)} /></div>
              <div><Label className="text-xs">Descuento cumpleaños (%)</Label>
                <Input type="number" value={auto.cumpleanos_descuento_pct} onChange={(e) => setAuto("cumpleanos_descuento_pct", parseInt(e.target.value) || 10)} /></div>
              <div><Label className="text-xs">Vigencia regalo cumpleaños (días)</Label>
                <Input type="number" value={auto.cumpleanos_vigencia_dias} onChange={(e) => setAuto("cumpleanos_vigencia_dias", parseInt(e.target.value) || 7)} /></div>
            </div>
            <div><Label className="text-xs">Link de la tienda en los mensajes</Label>
              <Input value={config.link_tienda || ""} onChange={(e) => setConfig({ ...config, link_tienda: e.target.value })} /></div>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-sm space-y-2">
            <h2 className="font-display text-lg text-torta-dark">Enviar mensaje de prueba</h2>
            <div className="flex gap-2">
              <Input data-testid="test-telefono-input" placeholder="Celular ej: 3196536874" value={testTel} onChange={(e) => setTestTel(e.target.value)} />
              <Button data-testid="test-enviar-btn" onClick={enviarTest} disabled={enviandoTest || !testTel}>
                {enviandoTest ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Probar WhatsApp
              </Button>
            </div>
            <p className="text-[11px] text-torta-muted">⚠️ Con Twilio en modo Trial/Sandbox los WhatsApp solo llegan a números que hayan activado el sandbox de Twilio. Haz upgrade de tu cuenta Twilio y registra un remitente WhatsApp para enviar a todos tus clientes.</p>
          </div>
        </TabsContent>

        <TabsContent value="segmentos" className="mt-4 space-y-4">
          {segmentos && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[["inactivo_30", "😶 30 días", segmentos.inactivo_30.total], ["inactivo_60", "😟 60 días", segmentos.inactivo_60.total],
                  ["inactivo_90", "🥺 90+ días", segmentos.inactivo_90.total], ["activos", "✅ Activos", segmentos.activos]].map(([k, l, v]) => (
                  <div key={k} className="bg-white rounded-2xl p-4 shadow-sm text-center" data-testid={`segmento-card-${k}`}>
                    <p className="text-2xl font-bold text-torta-dark">{v}</p>
                    <p className="text-xs text-torta-muted">{l}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[["🎂 Cumplen hoy", segmentos.cumplen_hoy], ["🎂 Con cumpleaños registrado", segmentos.con_cumpleanos],
                  ["⏰ Regalos por vencer", segmentos.beneficios_por_vencer], ["🔕 No recibir mensajes", segmentos.optout]].map(([l, v]) => (
                  <div key={l} className="bg-white rounded-2xl p-4 shadow-sm text-center">
                    <p className="text-2xl font-bold text-torta-dark">{v}</p>
                    <p className="text-xs text-torta-muted">{l}</p>
                  </div>
                ))}
              </div>
              {["inactivo_30", "inactivo_60", "inactivo_90"].map((k) => segmentos[k].clientes.length > 0 && (
                <div key={k} className="bg-white rounded-2xl p-4 shadow-sm">
                  <p className="text-sm font-bold text-torta-dark mb-2">{TIPO_LABEL[k]} — muestra</p>
                  {segmentos[k].clientes.map((c) => (
                    <div key={c.customer_id} className="flex justify-between text-xs py-1.5 border-b last:border-0 border-torta-sand">
                      <span className="text-torta-dark font-medium">{c.nombre} <span className="text-torta-muted font-normal">· {c.phone}</span></span>
                      <span className="text-torta-muted">{c.dias_inactivo} días sin comprar</span>
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}
        </TabsContent>

        <TabsContent value="plantillas" className="mt-4 space-y-4">
          <p className="text-xs text-torta-muted bg-amber-50 border border-amber-200 rounded-xl p-3">
            Variables disponibles: <code>{"{{nombre}}"}</code>, <code>{"{{nivel}}"}</code>, <code>{"{{beneficio}}"}</code>, <code>{"{{codigo}}"}</code>, <code>{"{{vencimiento}}"}</code>, <code>{"{{regalo}}"}</code>, <code>{"{{dias}}"}</code>, <code>{"{{link}}"}</code>
          </p>
          {plantillas.map((p, idx) => (
            <div key={p.id} className="bg-white rounded-2xl p-4 shadow-sm space-y-2" data-testid={`plantilla-${p.tipo}`}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-torta-dark">{TIPO_LABEL[p.tipo] || p.nombre}</p>
                <div className="flex items-center gap-2">
                  <Switch checked={p.activo} onCheckedChange={(v) => { const ps = [...plantillas]; ps[idx] = { ...p, activo: v }; setPlantillas(ps); }} />
                  <Button size="sm" variant="outline" data-testid={`guardar-plantilla-${p.tipo}`} onClick={() => guardarPlantilla(plantillas[idx])}><Save className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              <Textarea rows={3} value={p.mensaje} onChange={(e) => { const ps = [...plantillas]; ps[idx] = { ...p, mensaje: e.target.value }; setPlantillas(ps); }} />
            </div>
          ))}
        </TabsContent>

        <TabsContent value="historial" className="mt-4 space-y-3">
          {envios && (
            <>
              <div className="flex gap-2 text-center">
                {[["total", "Total"], ["enviados", "Enviados"], ["fallidos", "Fallidos"]].map(([k, l]) => (
                  <div key={k} className="flex-1 bg-white rounded-xl p-3 shadow-sm">
                    <p className="text-lg font-bold text-torta-dark">{envios.resumen[k]}</p>
                    <p className="text-[10px] text-torta-muted">{l}</p>
                  </div>
                ))}
              </div>
              {envios.ultima_ejecucion && (
                <p className="text-[11px] text-torta-muted">Última ejecución: {String(envios.ultima_ejecucion.fecha).slice(0, 16).replace("T", " ")} UTC</p>
              )}
              <div className="space-y-2 max-h-[480px] overflow-y-auto">
                {envios.envios.map((e) => (
                  <div key={e.id} className="bg-white rounded-xl px-3 py-2.5 shadow-sm" data-testid={`envio-${e.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`border-0 text-[10px] ${e.estado === "enviado" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-700"}`}>{e.estado}</Badge>
                      <span className="text-xs font-semibold text-torta-dark">{TIPO_LABEL[e.tipo] || e.tipo}</span>
                      <span className="text-xs text-torta-muted">{e.cliente_nombre || ""} · {e.phone_normalized}</span>
                      <span className="text-[10px] text-torta-muted ml-auto">{String(e.fecha).slice(0, 16).replace("T", " ")}</span>
                    </div>
                    <p className="text-[11px] text-torta-muted mt-1 line-clamp-2">{e.mensaje}</p>
                    {e.estado === "fallido" && <p className="text-[10px] text-red-600 mt-0.5">{e.detalle}</p>}
                  </div>
                ))}
                {envios.envios.length === 0 && <p className="text-center text-sm text-torta-muted py-8">Aún no se han enviado mensajes</p>}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
